import { useCallback } from 'react';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import { useAssetTransferAvailability } from '@/entities/account';
import { useInternalTransferModals } from '@/features/internal-transfer';
import { openTradingAccUrl } from '@/shared/lib';
import type {
  TradingAssetAccountEntry,
  TradingAssetActionsVisibility,
  TradingAssetRow,
} from './types';

const useTradingAssetActions = () => {
  const { session } = useSessionStore();
  const isVerified = session?.additional?.isVerified || false;

  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { openInternalTransferFormModal } = useInternalTransferModals();
  const { getAccountTypeTransferAvailability } = useAssetTransferAvailability();

  const getActionsVisibility = useCallback(
    (row: TradingAssetRow): TradingAssetActionsVisibility => ({
      trade: row.accounts.length > 0,
      transfer: row.types.some(type =>
        Boolean(getAccountTypeTransferAvailability(type))
      ),
    }),
    [getAccountTypeTransferAvailability]
  );

  const getAccountActionsVisibility = useCallback(
    (account: TradingAssetAccountEntry): TradingAssetActionsVisibility => ({
      trade: Boolean(account.tradingAccKey),
      transfer: Boolean(
        getAccountTypeTransferAvailability(account.accountType)
      ),
    }),
    [getAccountTypeTransferAvailability]
  );

  const onTrade = useCallback(
    (row: TradingAssetRow, account?: TradingAssetAccountEntry) => {
      if (!isVerified) {
        openVerificationInfoModal();
        return;
      }

      const target = account ?? row.accounts[0];

      if (!target?.tradingAccKey) {
        return;
      }

      openTradingAccUrl(String(target.tradingAccKey), target.accountType);
    },
    [isVerified, openVerificationInfoModal]
  );

  const onTransfer = useCallback(
    (row: TradingAssetRow, account?: TradingAssetAccountEntry) => {
      const target = account ?? row.accounts[0];

      if (!target) {
        return;
      }

      openInternalTransferFormModal({
        initialContext: {
          accountType: target.accountType,
          accountNumber: target.accNum,
          assetCode: row.code,
        },
      });
    },
    [openInternalTransferFormModal]
  );

  return {
    getActionsVisibility,
    getAccountActionsVisibility,
    onTrade,
    onTransfer,
  };
};

export { useTradingAssetActions };
